import { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Users2, Layers } from "lucide-react";
import { toast } from "sonner";

// Daily-soap multi-unit manager. Shows unit list, add/edit/delete rows.
// Mode:
//   shared_pool → unit is a scene-grouping label; HODs come from project roster
//   full_stack  → unit has its own HOD list (unit_hods = project_crew ids)
export default function MultiUnitDialog({ open, onOpenChange, projectId, onSaved }) {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/units`);
      setUnits(data.units || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  const remove = async (unitId) => {
    if (!confirm("Remove this shooting unit? Data stays hidden but is not deleted.")) return;
    try {
      await api.delete(`/projects/${projectId}/units/${unitId}`);
      toast.success("Unit removed");
      await load();
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0c] border border-white/10 text-white max-w-2xl"
        data-testid="tv-multi-unit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-xl">
            Shooting Units
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            Every project has a main unit by default. Add a Unit B / Chroma / Second unit
            here when your daily soap needs to shoot in parallel to hit the bank target.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
              Main unit + {units.length} additional
            </div>
            <Button onClick={() => setAddOpen(true)}
              className="btn-themed h-9 px-4 rounded-full text-[10px] uppercase tracking-[0.22em]"
              data-testid="tv-unit-add-btn">
              <Plus size={12} className="mr-1.5" /> Add Unit
            </Button>
          </div>

          {loading ? (
            <div className="text-sm text-white/45 py-6 text-center">Loading…</div>
          ) : (
            <div className="space-y-2.5" data-testid="tv-unit-list">
              <UnitRow name="Main Unit" mode="shared_pool" isMain />
              {units.map((u) => (
                <UnitRow key={u.id} id={u.id} name={u.name} mode={u.mode}
                  notes={u.notes} unitHods={u.unit_hods}
                  onRemove={() => remove(u.id)} />
              ))}
              {units.length === 0 && (
                <div className="text-xs text-white/45 py-4 text-center border border-dashed border-white/10 rounded-xl"
                  data-testid="tv-unit-empty">
                  Only the Main Unit is active. Add a Unit B when you need to
                  parallel-shoot scenes to catch up on your bank.
                </div>
              )}
            </div>
          )}
        </div>

        <AddUnitInner open={addOpen} onOpenChange={setAddOpen}
          projectId={projectId}
          onAdded={async () => { await load(); onSaved?.(); }} />
      </DialogContent>
    </Dialog>
  );
}

function UnitRow({ id, name, mode, notes, unitHods, isMain, onRemove }) {
  return (
    <div className="rounded-xl p-3.5 bg-white/[0.04] border border-white/10 flex items-center gap-3"
      data-testid={id ? `tv-unit-row-${id}` : "tv-unit-row-main"}>
      <div className="w-10 h-10 rounded-lg bg-white/8 flex items-center justify-center text-white/70">
        {mode === "full_stack" ? <Layers size={16} /> : <Users2 size={16} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-white">{name}</div>
        <div className="mt-0.5 text-[10px] tracking-[0.2em] uppercase text-white/55">
          {mode === "full_stack"
            ? `Full stack · ${(unitHods || []).length} HODs assigned`
            : "Shared pool with main unit"}
        </div>
        {notes && <div className="mt-1 text-[11px] text-white/45 truncate">{notes}</div>}
      </div>
      {!isMain && onRemove && (
        <button onClick={onRemove}
          className="text-white/40 hover:text-red-300 transition-colors"
          data-testid={`tv-unit-remove-${id}`}
          aria-label="Remove unit">
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
}

function AddUnitInner({ open, onOpenChange, projectId, onAdded }) {
  const [name, setName] = useState("");
  const [mode, setMode] = useState("shared_pool");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => { setName(""); setMode("shared_pool"); setNotes(""); };
  const submit = async () => {
    if (!name.trim()) {
      toast.error("Unit name is required");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/units`, {
        name: name.trim(), mode, notes: notes.trim(),
      });
      toast.success(`Added ${name}`);
      onOpenChange(false);
      reset();
      onAdded?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add unit");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="bg-[#0a0a0c] border border-white/10 text-white max-w-lg"
        data-testid="tv-unit-add-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-lg">
            Add a shooting unit
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Unit B · Chroma Unit · Second Unit"
              className="mt-2 h-11 bg-black/40 border-white/15"
              data-testid="tv-unit-name-input" autoFocus />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="mt-2 h-11 bg-black/40 border-white/15"
                data-testid="tv-unit-mode-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0c] border-white/15 text-white">
                <SelectItem value="shared_pool">
                  Shared pool — unit is a scene label; HODs from main crew
                </SelectItem>
                <SelectItem value="full_stack">
                  Full stack — unit has its own HODs (own 1st AD, DoP, etc.)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Notes (optional)
            </Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="e.g. Runs Mon–Wed, indoor bungalow, ~6 scenes/day"
              className="mt-2 bg-black/40 border-white/15 text-sm"
              data-testid="tv-unit-notes-input" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => onOpenChange(false)}
              className="h-11 px-5 text-xs uppercase tracking-[0.22em]"
              data-testid="tv-unit-cancel">
              Cancel
            </Button>
            <Button onClick={submit} disabled={saving || !name.trim()}
              className="btn-themed h-11 px-6 rounded-full text-xs uppercase tracking-[0.25em]"
              data-testid="tv-unit-submit">
              {saving ? "Adding…" : "Add Unit"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
